import { setCookie, parseCookies } from 'nookies'
import { useAllowedQuery, useCheckoutState, useCohort, useCountry, useExperiment, useInitialData } from '../state'
import { Route } from '../config/routes'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { USER_JOURNEY_NAMES } from '../integrations/userJourney/constants'
import { getExpiredJourneyTimeoutTerminationReason } from '../integrations/userJourney/timeout'
import { isMarkerFromCurrentPageLoad, readJourneyMarker } from '../integrations/userJourney/journeyMarker'
import { JourneyOutcome } from '../integrations/userJourney/types'
import { useUserJourneyContext } from '../integrations/userJourney/UserJourneyContext'
import { useCheckoutJourneyOwnership } from '../integrations/userJourney/MobileUserJourneyOwnership'
import { getCheckoutJourneyTags } from '../integrations/userJourney/checkoutTags'
import {
  isCardScanRequiredErrorCode,
  isCheckoutConfirm2FARequired,
  isCheckoutConfirmEFARequired,
  nonTerminalErrors,
  parseQueryStateToUrl,
  SHOW_NEW_CHECKOUT_COOKIE_NAME,
} from '../model'

const DEFAULT_PROCESSING_REDIRECTION_DELAY = 4000

export const browserRedirect = (destination: string, isZoidProtocol: boolean = false): void => {
  const cookies = parseCookies()
  if (!(cookies[SHOW_NEW_CHECKOUT_COOKIE_NAME] === 'true')) {
    setCookie(null, SHOW_NEW_CHECKOUT_COOKIE_NAME, JSON.stringify(true), {
      maxAge: 60 * 60,
      sameSite: 'lax',
      path: '/',
    })
  }

  if (isZoidProtocol && typeof window.xprops !== 'undefined') {
    ;(window.xprops.onRecreate as (destination: string) => void)(destination)
    return
  }

  window.location.href = destination
}

export const useRedirectionDelay = (): number => {
  const redirectionDelay = useExperiment('ProcessingRedirectionDelay')

  if (!redirectionDelay.active) {
    return DEFAULT_PROCESSING_REDIRECTION_DELAY
  }

  switch (redirectionDelay.value) {
    case 'A':
      return 1000
    case 'B':
      return 2000
    case 'D':
      return 3000
    default:
      return DEFAULT_PROCESSING_REDIRECTION_DELAY
  }
}

export const useXbtBrowserRedirect = (): typeof browserRedirect => {
  const initialData = useInitialData()
  const { checkout } = useCheckoutState()
  const cohort = useCohort()
  const country = useCountry()
  const isZoidProtocol = initialData?.protocol === 'zoid'
  const { addTag, setVariant, completeJourney, getActiveJourney, resumeJourney, startJourney } = useUserJourneyContext()
  const { claimRocketshipOwnership, owner, reportingEligibilityResolved, waitForOwnershipResolution, waitForReportingEligibility } =
    useCheckoutJourneyOwnership()

  const allowedQueryParam = useAllowedQuery()
  const queryStringToAdd = parseQueryStateToUrl(allowedQueryParam)

  const redirect = async (destination: string, skipPrefill: boolean): Promise<void> => {
    let url = skipPrefill ? `${destination}&skipPrefill=true` : destination
    url = queryStringToAdd !== '' ? `${url}&${queryStringToAdd}` : url
    let active = getActiveJourney()
    let journeyTimeoutReason = active?.name === USER_JOURNEY_NAMES.CHECKOUT ? getExpiredJourneyTimeoutTerminationReason(active.created) : undefined
    let claimedJourneyForRedirect = false
    let journeyExistingTags: readonly string[] = active?.name === USER_JOURNEY_NAMES.CHECKOUT ? active.tags : []
    if (active?.name !== USER_JOURNEY_NAMES.CHECKOUT) {
      let boundary = claimRocketshipOwnership()
      if (owner === 'negotiating' && boundary === undefined) {
        await waitForOwnershipResolution()
        active = getActiveJourney()
        if (active?.name === USER_JOURNEY_NAMES.CHECKOUT) {
          journeyExistingTags = active.tags
          journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(active.created)
        } else {
          boundary = claimRocketshipOwnership()
        }
      }
      if (boundary !== undefined) {
        claimedJourneyForRedirect = true
        const marker = readJourneyMarker()
        const resumesCurrentBoundary =
          marker?.handoff !== true &&
          marker?.journey.name === USER_JOURNEY_NAMES.CHECKOUT &&
          boundary.journeyId !== undefined &&
          marker.journey.id === boundary.journeyId
        if (resumesCurrentBoundary) {
          resumeJourney(marker.journey, marker.esid)
          journeyExistingTags = marker.journey.tags
          journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(marker.journey.created)
        } else if (marker !== undefined && marker.handoff !== true) {
          resumeJourney(marker.journey, marker.esid)
          completeJourney(marker.journey.name, isMarkerFromCurrentPageLoad(marker) ? JourneyOutcome.PROCESS_CRASHED : JourneyOutcome.PROCESS_KILLED)
        }
        if (!resumesCurrentBoundary) {
          startJourney(USER_JOURNEY_NAMES.CHECKOUT, boundary.startedAtEpochMs, boundary.journeyId)
          journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(boundary.startedAtEpochMs)
        }
      }
    }
    if (claimedJourneyForRedirect || active?.name === USER_JOURNEY_NAMES.CHECKOUT) {
      for (const [key, value] of getCheckoutJourneyTags({
        merchantId: checkout?.merchant.id,
        country,
        cohort,
        existingTags: journeyExistingTags,
        initial: initialData,
      })) {
        addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
      }
    }
    if (journeyTimeoutReason !== undefined) {
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, journeyTimeoutReason)
    } else {
      setVariant(USER_JOURNEY_NAMES.CHECKOUT, undefined)
      addTag(USER_JOURNEY_NAMES.CHECKOUT, 'crossRegion:true')
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.SUCCEEDED)
    }
    browserRedirect(url, isZoidProtocol)
  }

  return (destination: string, skipPrefill = false): void => {
    if (reportingEligibilityResolved) {
      void redirect(destination, skipPrefill)
      return
    }
    void waitForReportingEligibility().then(() => void redirect(destination, skipPrefill))
  }
}

interface ConfirmCheckoutErrorHandlingRequest {
  errorCode: ErrorCode
  returnRoute?: Route
}
interface ConfirmCheckoutErrorHandlingResponse {
  route?: Route
  messageKey?: string
}

export const getConfirmCheckoutErrorHandling = ({ errorCode, returnRoute }: ConfirmCheckoutErrorHandlingRequest): ConfirmCheckoutErrorHandlingResponse => {
  const isCardScanRequired = isCardScanRequiredErrorCode(errorCode)
  const is2FARequired = isCheckoutConfirm2FARequired(errorCode)
  const isEFARequired = isCheckoutConfirmEFARequired(errorCode)

  if (!nonTerminalErrors.includes(errorCode)) {
    return {
      route: Route.TERMINAL_ERROR,
    }
  } else if (is2FARequired || isEFARequired) {
    return {
      route: Route.VERIFY,
    }
  } else if (isCardScanRequired) {
    return {
      route: Route.CARD_SCAN,
    }
  } else if (errorCode === ErrorCode.ComplianceDeclineSystemError) {
    return {
      messageKey: 'summary:complianceDeclineSystemError',
      route: returnRoute ?? Route.TERMINAL_ERROR,
    }
  } else {
    return {}
  }
}
